import cloneDeep from 'lodash/cloneDeep';
import { defineStore } from 'pinia';

import { useWorkflowStore } from './workflow';
import { useWorkflowGraph } from './workflow-graph';

import type { FlowNode, TriggerType } from '../types/workflow';

type HistoryTransactionItem = {
  id: string | number;
  before?: FlowNode;
  after?: FlowNode;
};

type HistoryTransaction = HistoryTransactionItem[];

const MAX_HISTORY = 200;

const updateSnapshotData = (nodeSnapshot: any, newData: any, dataKey?: 'name' | 'trigger' | 'data') => {
  const routerChildren = nodeSnapshot.children ? [...nodeSnapshot.children] : [];

  switch (dataKey) {
    case 'name':
      nodeSnapshot.name = newData as string;
      break;
    case 'trigger':
      nodeSnapshot.triggerType = newData as TriggerType;
      break;
    case 'data':
      if (nodeSnapshot.category === 'Router' && nodeSnapshot.type === 'Branch') {
        const currentBranches = newData.branches || [];
        const snapshotBranches = nodeSnapshot.data.branches || [];

        // Map over the children expected by the SNAPSHOT (which includes undone branches)
        const mergedBranches = routerChildren
          .map((childId) => {
            // 1. Try to find the branch in the NEW data (the update)
            const freshData = currentBranches.find((b: any) => b.connectorId === childId);

            // 2. If not found (branch is currently undone), retrieve it from the SNAPSHOT data
            const oldData = snapshotBranches.find((b: any) => b.connectorId === childId);

            return freshData || oldData;
          })
          .filter(Boolean); // Remove undefined entries

        nodeSnapshot.data = cloneDeep({ ...nodeSnapshot.data, branches: mergedBranches });
      } else if (nodeSnapshot.category === 'Router' && nodeSnapshot.type === 'AskQuestion') {
        const hasTimeoutBranch = routerChildren.length === 3;
        if (hasTimeoutBranch) {
          nodeSnapshot.data = cloneDeep({ ...nodeSnapshot.data, ...newData });
          nodeSnapshot.data.timeout.hasTimeout = true;
        } else {
          nodeSnapshot.data = cloneDeep({
            ...nodeSnapshot.data,
            ...newData,
            timeout: { hasTimeout: false, value: newData.timeout.value, unit: newData.timeout.unit },
          });
          nodeSnapshot.data.timeout.hasTimeout = false;
        }
      } else {
        nodeSnapshot.data = cloneDeep({ ...nodeSnapshot.data, ...newData });
      }
      break;
  }
};

export const useWorkflowHistory = defineStore('bvWorkflowHistory', {
  state: () => ({
    // the action currently being recorded (a group of changes)
    currentHistoryTransaction: undefined as HistoryTransaction | undefined,
    // past actions you can go back through
    undoStack: [] as HistoryTransaction[],
    // undone actions that you can re-apply
    redoStack: [] as HistoryTransaction[],
  }),
  getters: {
    canUndo: (state) => state.undoStack.length > 0,
    canRedo: (state) => state.redoStack.length > 0,
  },
  actions: {
    // take the last transaction from undoStack, for each modified item => restore the 'before' state (if it's undefined delete node)
    undo() {
      const store = useWorkflowStore();
      const lastTransaction = this.undoStack.pop();
      if (!lastTransaction) {
        return;
      }

      lastTransaction.forEach(({ id, before }: HistoryTransactionItem) => {
        this.applyState(id, before);
      });

      this.redoStack.push(lastTransaction);

      // open side panel with last node.
      store.selectNode(store.lastNode!.id);
    },

    // take the last transaction from redoStack, for each modified item => restore the 'after' state
    redo() {
      const store = useWorkflowStore();
      const lastTransaction = this.redoStack.pop();
      if (!lastTransaction) {
        return;
      }

      lastTransaction.forEach(({ id, after }: HistoryTransactionItem) => {
        this.applyState(id, after);
      });

      this.undoStack.push(lastTransaction);

      // open side panel with last node.
      store.selectNode(store.lastNode!.id);
    },

    // Records the before snapshot of the node being changed.
    addToHistoryTransaction(id: string | number, createdByPaste?: boolean) {
      const store = useWorkflowStore();
      // Start transaction if none active
      if (!this.currentHistoryTransaction) {
        this.startHistoryTransaction();
      }

      const exists = this.currentHistoryTransaction!.some((item) => item.id === id);
      if (exists) {
        return; // Skip adding it again
      }

      const workFlowStep = store.flow[id];
      if (!createdByPaste) {
        this.currentHistoryTransaction!.push({
          id,
          before: workFlowStep ? cloneDeep(workFlowStep) : undefined,
        });
      } else {
        // In Clipboard, by the time instantiateSnapshot returns the id to us, the node has already been added to the flow.
        // so nodes created by paste their 'before' state is recorded as the created node object (instead of undefined).
        this.currentHistoryTransaction!.push({
          id,
          before: undefined, // we want to force the before state to be undefined in this case.
        });
      }
    },

    // Only for safety, if there is an unfinshed transaction, close it first and creates an empty transaction array before we start adding to it.
    startHistoryTransaction() {
      if (this.currentHistoryTransaction) {
        this.endHistoryTransaction();
      }
      this.currentHistoryTransaction = [];
    },

    // For each item in the transaction, it grabs the new after state from store.flow
    endHistoryTransaction() {
      const store = useWorkflowStore();
      if (!this.currentHistoryTransaction) {
        return;
      }

      // Fill 'after' snapshots
      this.currentHistoryTransaction.forEach((c: HistoryTransactionItem) => {
        const workFlowStep = store.flow[c.id];
        c.after = workFlowStep ? cloneDeep(workFlowStep) : undefined;
      });

      this.undoStack.push(cloneDeep(this.currentHistoryTransaction));

      if (this.undoStack.length > MAX_HISTORY) {
        this.undoStack.shift();
      }

      this.redoStack.length = 0;
      this.currentHistoryTransaction = undefined;
    },

    replaceNodeDataInHistory(id: string | number, newData: any, dataKey?: 'name' | 'trigger' | 'data') {
      const allTransactions = [...this.undoStack, ...this.redoStack];

      allTransactions.forEach((transaction) => {
        transaction.forEach((item) => {
          if (item.id === id) {
            if (item.before) {
              updateSnapshotData(item.before, newData, dataKey);
            }
            if (item.after) {
              updateSnapshotData(item.after, newData, dataKey);
            }
          }
        });
      });
    },

    applyState(id: string | number, stateToApply?: FlowNode) {
      const graphStore = useWorkflowGraph();
      const store = useWorkflowStore();
      if (stateToApply) {
        const nodeSnapshot = cloneDeep(stateToApply);

        // If the node already exists (e.g., a parent node being updated by a child's restoration), we MUST update the existing object in place.
        // Replacing it breaks the Graph Component's reactivity because the ID (key) didn't change, preventing a re-render.
        if (graphStore.flow[id]) {
          Object.assign(graphStore.flow[id], nodeSnapshot);
        } else {
          // If it doesn't exist, create it fresh.
          graphStore.flow[id] = nodeSnapshot;
        }
      } else {
        delete graphStore.flow[id];
        store.clearNodeErrors(id);
      }
    },

    resetHistory() {
      this.undoStack = [];
      this.redoStack = [];
      this.currentHistoryTransaction = undefined;
    },
  },
});
